import Row from "react-bootstrap/Row";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import {FieldError, Path, useFormContext} from "react-hook-form";
import {MANDATORY_ICON} from "../FormComponents";
import React, {ReactNode, useState} from "react";

/**
 * Props for this component
 */
interface TextProps<T extends Record<string, any>> {
  label: string;
  helpText?: ReactNode;
  error: FieldError | undefined;
  fieldId: Path<T>;
  disabled?: boolean;
  mandatory?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  customValidation?: (v: string) => true | string;
  textArea?: boolean;
  charCount?: boolean;
  sideAction?: ReactNode;
}

/**
 * This is a component that will add a simple text box field. It was designed to be used as part of a form panel. You may
 * pass the following props:
 *
 * @param label The text to show before the text box. (By default this will show to the left of the input
 * @param helpText Subtext that shows on top of the question
 * @param error The fieldError from the React useForm specific for this field
 * @param fieldId The ID of the field as defined in the form interface
 * @param disabled <i>Optional</i> A boolean to mark the field as disabled
 * @param mandatory <i>Optional</i> A boolean to mark the field as mandatory
 * @param minLength <i>Optional</i> The minimum number of chars this field can take
 * @param maxLength <i>Optional</i> The maximum number of chars this field can take
 * @param pattern <i>Optional</i> A pattern the input will have to follow
 * @param customValidation <i>Optional</i> A function where we can provide the component some more specific validations
 * @param textArea <i>Optional</i> Transforms this text input into a text area
 * @param charCount <i>Optional</i> Shows a character counter
 * @param sideAction <i>Optional</i> If more action is needed to be associated with this text box it can be passed as a React component here
 */
export default function Text<T extends Record<string, any>>({
                                                              label,
                                                              helpText,
                                                              error,
                                                              fieldId,
                                                              disabled = false,
                                                              mandatory = false,
                                                              minLength,
                                                              maxLength,
                                                              pattern,
                                                              customValidation,
                                                              textArea = false,
                                                              charCount = false,
                                                              sideAction
                                                            }: TextProps<T>) {
  const [count, setCount] = useState<number>(0);
  const {register} = useFormContext<T>();

  // This is to be used when generating data-test-ids
  const fieldIdForDataTest = label.toLowerCase().replace(" ", "-");

  return <Form.Group as={Row} className="mb-2" controlId={`formHorizontal${fieldId}`}>
    {helpText && <Row>
      <Col sm={2}></Col>
      <Col sm={10} className={"question-help-text"}>
        {helpText} {charCount && (
        maxLength ?
          <span className={"char-counter"}>{`${maxLength - count} characters left`}</span>
          :
          <span className={"char-counter"}>{`${count} characters`}</span>
      )}
      </Col>
    </Row>}
    <Row>
      <Form.Label column sm={2}>
        {label} {mandatory && <MANDATORY_ICON/>}
      </Form.Label>
      <Col>
        <Form.Control
          type="text"
          as={textArea ? "textarea" : undefined}
          rows={textArea ? 3 : undefined}
          placeholder={label}
          disabled={disabled}
          {...register(fieldId, {
            onChange: (v) => {
              setCount(v.target.value.length)
            },
            // Normalize value
            setValueAs: (v) => {
              const trimmed = v.trim();
              return (mandatory) ?
                trimmed :
                trimmed === "" ? undefined : trimmed;
            },
            required: mandatory ? `${label} is required` : false,
            minLength: minLength ? {
              value: minLength,
              message: `${label} must be at least ${minLength} characters`
            } : undefined,
            maxLength: maxLength ? {
              value: maxLength,
              message: `${label} must not be longer than ${maxLength} characters`
            } : undefined,
            pattern: pattern ? {
              value: pattern,
              message: `${label} must be in the correct format`
            } : undefined,
            validate: customValidation ?
              (v: string) => customValidation(v) :
              undefined
          })}
          isInvalid={!!error}
          data-testid={fieldIdForDataTest}
        />
        <Form.Control.Feedback type="invalid" data-testid={`${fieldIdForDataTest}-error`}>
          {error?.message}
        </Form.Control.Feedback>
      </Col>
      {(sideAction && !disabled) && <>
        {sideAction}</>}
    </Row>
  </Form.Group>;
}